import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Hourglass,
  Loader2,
  Mic,
  Search,
  ShieldAlert,
  StopCircle,
  Users,
  Zap,
} from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import {
  computeLabelBudgets,
  pickStageShape,
  useStageLayout,
} from "../../hooks/useStageLayout";
import { usePreviewedCouncil } from "../../hooks/usePreviewedCouncil";
import { selectCurrentTurn, selectSessionLifecycle } from "../../store/selectors";
import { AgentAvatar } from "./AgentAvatar";
import { AvatarAllocationProvider } from "./AvatarAllocationContext";
import { CenterStageSpotlight } from "./CenterStageSpotlight";
import { TopologyEdgeRenderer } from "./TopologyEdgeRenderer";
import { Pill } from "../common/Pill";
import { TermPill } from "../common/TermPill";
import type { Persona, TurnPhase } from "../../domain/types";
import { cn } from "../common/cn";
import { t } from "../../i18n";

// Konsey Cognitive Renaissance — Bölüm B1 follow-up.
// Operatör şikayeti: "koca ekranda ortada çük gibi". Önceki MIN/MAX
// değerleri laptop ekranlarında bile sahneyi 360-500 px aralığına
// sıkıştırıyordu — avatar conic border + halo + label tüm bu alanı
// yiyor, gerçek tartışma alanı 200 px'e düşüyordu.
//
// Yeni değerler:
//   • MIN_WIDTH 320 → 540 — wizard preview anında bile ciddi bir
//     boyut. Mobile dışında her viewport rahat sığar.
//   • MAX_WIDTH 760 → 1100 — masaüstünde sahnenin gerçek hâkimiyeti
//     hissedilsin (FHD ekran orchestrator card'ı ~1100 px verir).
const MIN_WIDTH = 540;
const MAX_WIDTH = 1100;
// In presentation mode the stage absorbs the full cinematic canvas.
//
// 2026-05-b ↑ from 1600 → 2400.
//   The 1600-px ceiling was still leaving "halo" pixels on UHD
//   (3840×2160) projector setups when the operator wanted the round
//   table to genuinely fill the cinematic canvas. The ceiling is
//   now effectively academic for all common displays — the
//   dual-axis container clamp below picks whichever of
//   (parentWidth, parentHeight/AR, ceiling) is tightest, so
//   real-world rendering is governed by the parent box, not by the
//   constant. The constant only matters when the parent grid cell
//   itself is enormous (8K stages, ultra-wide demo rigs) and we
//   still want a sane upper bound to keep the SVG edge renderer's
//   work O(seats), not O(pixels²).
const PRESENT_MAX_WIDTH = 2400;
// Boardroom (≥9 koltuk) aspect ratio.
//
// 2026-05-d revize: önceki 0.72 (sunum) sahneyi YATAY OLARAK
// daraltıyordu çünkü `width = parent_h / AR`. AR ne kadar büyükse
// width o kadar küçük (height-bound viewport'larda). Sunum modunda
// LiveMetricsBar + BigSpeakerCompact 100+ px yer kapladığı için
// parent_h ~600 px → width 833. Daha alçak AR ile width daha
// büyük, sahne yatay-cömert olur.
//
//   • Normal mod : 0.55 (≈ 16:9 yatay-cömert)
//   • Sunum mod  : 0.50 (daha yatay, full-bleed)
// Avatar layout zaten boardroom shape'i için iki sıra (üst+alt edge).
const BOARDROOM_AR_NORMAL = 0.55;
const BOARDROOM_AR_PRESENT = 0.50;
const GHOST_SEATS = 5;

function GhostSeat({ size, idx, total }: { size: number; idx: number; total: number }) {
  const angle = -Math.PI / 2 + (2 * Math.PI * idx) / total;
  // 28 + 30 = 58, matching the formula in the live render path
  // (avatarSize/2 + label-bleed) so ghosts and live seats sit on
  // the same circle.
  const radius = size / 2 - 58;
  const x = radius * Math.cos(angle);
  const y = radius * Math.sin(angle);
  return (
    <div
      className="absolute"
      style={{
        left: `calc(50% + ${x}px)`,
        top: `calc(50% + ${y}px)`,
        transform: "translate(-50%, -50%)",
      }}
    >
      <div className="flex flex-col items-center gap-1.5">
        <div
          className="rounded-full border border-dashed border-line-soft/70 bg-surface-2/40"
          style={{ width: 56, height: 56 }}
          aria-hidden
        />
        <div className="h-1.5 w-12 rounded-full bg-line-soft/40" aria-hidden />
        <div className="h-1.5 w-8 rounded-full bg-line-soft/30" aria-hidden />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * PhaseTheme — single source of truth for the per-phase visual contract
 * (label, icon, accent colour, glow tone). Centralising these mappings
 * means the BoardroomStage, the present-mode HUD, the transcript chips,
 * and any future telemetry overlay all speak the same dialect when they
 * surface "what is the council doing right now" affordances.
 *
 * The colours are deliberately Tailwind class strings so consumers stay
 * in the design-token system; raw hex values would couple the theme to
 * the pixel and break the dark/light theme switch.
 *
 * Why an icon component, not an emoji glyph
 * -----------------------------------------
 * Emojis render inconsistently across the OS font stack we ship to
 * (Windows Segoe UI Emoji vs. macOS Apple Color Emoji vs. Linux
 * Noto Color Emoji), break in the cinematic dark theme, and don't
 * inherit the `currentColor` of the surrounding text. lucide-react
 * icons solve all three at once.
 */
type PhaseThemeKey =
  | "speaking"
  | "retrieving"
  | "critiquing"
  | "yielding"
  | "idle";

interface PhaseThemeEntry {
  label: string;
  Icon: typeof Mic;
  text: string; // Tailwind text colour
  glow: string; // hsl triplet for halos / accents
  ringTint: string;
}

const PHASE_THEME: Record<PhaseThemeKey, PhaseThemeEntry> = {
  speaking: {
    label: "Canlı Konuşuyor",
    Icon: Mic,
    text: "text-rose-300",
    glow: "352 88% 62%",
    ringTint: "rose",
  },
  retrieving: {
    label: "Kaynak Araştırıyor",
    Icon: Search,
    text: "text-sky-300",
    glow: "210 92% 62%",
    ringTint: "sky",
  },
  critiquing: {
    label: "Kritik Hazırlıyor",
    Icon: Zap,
    text: "text-amber-300",
    glow: "38 92% 58%",
    ringTint: "amber",
  },
  yielding: {
    label: "Sözü Devrediyor",
    Icon: CheckCircle2,
    text: "text-emerald-300",
    glow: "152 70% 52%",
    ringTint: "emerald",
  },
  idle: {
    label: "Hazırlanıyor",
    Icon: Hourglass,
    text: "text-white/55",
    glow: "220 14% 60%",
    ringTint: "neutral",
  },
};

function resolvePhaseTheme(phase: TurnPhase | null | undefined): PhaseThemeEntry {
  if (!phase) return PHASE_THEME.idle;
  return PHASE_THEME[phase as PhaseThemeKey] ?? PHASE_THEME.idle;
}

/* ─────────────────────────────────────────────────────────────────────────
 * BrainBadgeOverlay — avatar üstünde küçük nabız (cognitive_load göstergesi).
 *
 * Konsey Cognitive Renaissance — Bölüm D5.
 *
 *   • cognitive_load < 50  → görünmez (UI noise yok)
 *   • 50 ≤ load < 70       → mavi yumuşak nabız
 *   • 70 ≤ load < 90       → turuncu nabız
 *   • load ≥ 90            → kırmızı agresif nabız + tooltip
 *
 * Boyut sahne avatarı boyutuna orantılı (proportional, min 8 max 16 px).
 * Tıklanınca BrainModal açar (avatar onClick ile aynı path).
 * ──────────────────────────────────────────────────────────────────────── */
function BrainBadgeOverlay({
  personaId,
  avatarSize,
  onClick,
}: {
  personaId: string;
  avatarSize: number;
  onClick: () => void;
}) {
  const cogLoad = useDebateStore(
    (s) => s.brainSnapshots[personaId]?.prefrontal.cognitive_load_pct ?? 0,
  );
  if (cogLoad < 50) return null;
  const tone =
    cogLoad >= 90
      ? { bg: "bg-rose-500", ring: "ring-rose-400/60", title: "Yüksek bilişsel yük" }
      : cogLoad >= 70
      ? { bg: "bg-amber-400", ring: "ring-amber-300/60", title: "Bilişsel yük yüksek" }
      : { bg: "bg-sky-400", ring: "ring-sky-300/60", title: "Bilişsel yük orta" };
  const badgePx = Math.max(8, Math.min(16, avatarSize * 0.1));
  const offset = Math.max(6, avatarSize * 0.05);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="focus-ring absolute z-20 cursor-pointer"
      style={{
        top: offset,
        right: offset,
        width: badgePx + 4,
        height: badgePx + 4,
      }}
      title={`${tone.title} (%${Math.round(cogLoad)}) — bilişsel profili göster`}
      aria-label={`${tone.title} %${Math.round(cogLoad)}`}
    >
      <span className="relative inline-flex h-full w-full">
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full ${tone.bg} opacity-75`}
        />
        <span
          className={`relative inline-flex h-full w-full rounded-full ring-2 ${tone.bg} ${tone.ring}`}
        />
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * CenterMonogram — dekoratif merkez ankrajı.
 *
 * Konsey Cognitive Renaissance — Bölüm B1: kullanıcı net olarak "ortada
 * speaker olmayacaktı" dedi. CenterPhaseBoard yerine, aktif konuşmacı
 * varken merkez tamamen sade. DAI Consensus marka monogramı (`D⌬`) +
 * yumuşak persona-glow nabız efekti. Tıklanmaz, hover yok, pasif.
 *
 * Boyut: 64px (sahne 200-1600 px arasında değişse bile orantısız
 * görünmez). Opacity 0.18 — varlığı hissedilir ama dikkati çekmez.
 * ──────────────────────────────────────────────────────────────────────── */
function CenterMonogram() {
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center z-[5]">
      <div
        className="grid h-16 w-16 place-items-center rounded-2xl"
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--accent) / 0.10) 0%, hsl(var(--accent-2) / 0.04) 100%)",
          boxShadow:
            "0 0 24px -6px hsl(var(--accent) / 0.18), inset 0 0 0 1px hsl(var(--accent) / 0.10)",
          opacity: 0.55,
          animation: "idle-breathe 5.2s ease-in-out infinite",
        }}
        aria-hidden
      >
        <svg
          viewBox="0 0 32 32"
          width={28}
          height={28}
          fill="none"
          stroke="hsl(var(--accent))"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.85}
        >
          <path d="M6 6h10c5 0 9 4.5 9 10s-4 10-9 10H6z" />
          <circle cx="16" cy="16" r="3" fill="hsl(var(--accent) / 0.22)" stroke="none" />
        </svg>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * CenterPhaseBoard — replaces the old "speaker name + expertise" card
 * that used to sit at the dead-centre of the round table.
 *
 * Why the swap (operator request, 2026-05):
 *   The persona affordance is ALREADY rendered in three other layers
 *   (avatar disc on the rim, persona-themed halo on the active seat,
 *   and the BigSpeakerStage portrait). Repeating the speaker's name
 *   in the centre of the table was pure visual duplication and
 *   stole pixels that should communicate WHAT the council is
 *   doing right now — research vs. critique vs. live speech.
 *
 * Visual contract:
 *   • Large phase chip: phase icon + uppercase phase label.
 *   • Tiny "için" subtitle naming the persona the phase belongs to
 *     (one line, truncated). This keeps the binding between phase
 *     and persona without re-shouting the persona's name.
 *   • For the live-speaking phase the chip wears the rose accent
 *     and a five-bar `wave-organic` strip so the audience feels the
 *     "broadcast on" energy.
 *
 * Idle / preview / ghost states preserve the legacy hints so we don't
 * regress the three "table is loading" affordances the engine ships.
 */
function CenterPhaseBoard({
  speaker,
  previousSpeaker,
  ghostMode,
  isLive,
  phase,
}: {
  speaker: Persona | null;
  previousSpeaker: Persona | null;
  ghostMode: boolean;
  isLive: boolean;
  phase: TurnPhase;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center z-10">
      <AnimatePresence mode="wait">
        {speaker ? (
          (() => {
            const theme = resolvePhaseTheme(phase);
            const PhaseIcon = theme.Icon;
            const isLiveSpeech = phase === "speaking";
            return (
              <motion.div
                key={`${speaker.id}-${phase}`}
                className="flex flex-col items-center gap-2 rounded-2xl border border-white/[0.10] bg-[hsl(220_20%_6%/0.86)] px-5 py-3 backdrop-blur-xl shadow-[0_10px_44px_-10px_rgb(0_0_0/0.7)] max-w-[18rem]"
                style={{
                  boxShadow: `0 10px 44px -10px rgb(0 0 0 / 0.7), 0 0 0 1px hsl(${theme.glow} / 0.22)`,
                }}
                initial={{ opacity: 0, scale: 0.85, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -4 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* ── Phase status header ─────────────────────────────── */}
                <div className="flex items-center gap-2.5">
                  {isLiveSpeech ? (
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
                    </span>
                  ) : (
                    <PhaseIcon
                      className={`h-3.5 w-3.5 ${theme.text} ${
                        phase === "retrieving" || phase === "critiquing"
                          ? "animate-pulse"
                          : ""
                      }`}
                      aria-hidden
                    />
                  )}
                  <span
                    className={`text-[10px] font-bold uppercase tracking-[0.24em] ${theme.text}`}
                  >
                    {theme.label}
                  </span>
                </div>

                {/* ── Active waveform strip — only during live speech ──── */}
                {isLiveSpeech && (
                  <div
                    className="wave-organic h-4"
                    style={{ color: `hsl(${theme.glow})` }}
                  >
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                )}

                {/* ── Subtitle — which persona this phase belongs to ──── */}
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-[9px] uppercase tracking-[0.18em] text-white/35 font-medium">
                    Şu konsey üyesi adına
                  </span>
                  <span className="max-w-[16rem] truncate text-[12.5px] font-semibold text-white/85">
                    {speaker.displayName}
                  </span>
                </div>
              </motion.div>
            );
          })()
        ) : ghostMode ? (
          <motion.div
            key="ghost"
            className="flex flex-col items-center gap-2 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Users className="h-5 w-5 text-fg-subtle" aria-hidden />
            <span className="text-xs text-fg-subtle">{t("stage.waitingNoQuery")}</span>
          </motion.div>
        ) : !isLive ? (
          <motion.div
            key="preview"
            className="flex max-w-[14rem] flex-col items-center gap-2 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Users className="h-5 w-5 text-accent-300" aria-hidden />
            <span className="text-xs leading-snug text-fg-muted text-balance">
              {t("stage.waitingReady")}
            </span>
          </motion.div>
        ) : previousSpeaker ? (
          <motion.div
            key="prev"
            className="flex flex-col items-center gap-2 rounded-2xl border border-white/[0.06] bg-[hsl(220_20%_8%/0.7)] px-4 py-2.5 backdrop-blur-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Hourglass className="h-3.5 w-3.5 text-white/45 animate-pulse" aria-hidden />
            <span className="text-[10px] uppercase tracking-[0.2em] text-fg-subtle/80">
              Sıradaki hazırlanıyor
            </span>
          </motion.div>
        ) : (
          <motion.div
            key="silent"
            className="flex flex-col items-center gap-1 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Users className="h-5 w-5 text-fg-subtle" aria-hidden />
            <span className="text-[11px] uppercase tracking-wider text-fg-subtle">
              {t("stage.silent")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function RoundtableStage() {
  const seatOrder = useDebateStore((s) => s.seatOrder);
  const personas = useDebateStore((s) => s.personas);
  const topology = useDebateStore((s) => s.topology);
  const query = useDebateStore((s) => s.query);
  const lifecycle = useDebateStore(selectSessionLifecycle);
  const currentSpeakerId = useDebateStore((s) => s.currentSpeakerId);
  const previousSpeakerId = useDebateStore((s) => s.previousSpeakerId);
  const audibleSpeakerId = useDebateStore((s) => s.audibleSpeakerId);
  const muted = useDebateStore((s) => s.muted);
  const roundIndex = useDebateStore((s) => s.roundIndex);
  const maxRounds = useDebateStore((s) => s.maxRounds);
  const finalizeReason = useDebateStore((s) => s.finalizeReason);
  const status = useDebateStore((s) => s.status);
  const presentMode = useDebateStore((s) => s.presentMode);
  const currentTurn = useDebateStore(selectCurrentTurn);
  const currentPhase: TurnPhase = currentTurn?.phase ?? "idle";

  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  // Track the RAW container box so the layout math below can clamp
  // against BOTH axes — a circle is square, so a 1600-px-wide cell
  // inside a 700-px-tall row would otherwise render at 1600×1600 and
  // push everything off-screen. Storing width AND height lets us
  // pick the tightest constraint per shape.
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({
    w: 380,
    h: 380,
  });
  // Larger ceiling in present mode so the table fills the cinematic
  // canvas and avatars stay legible from across the room.
  const maxWidth = presentMode ? PRESENT_MAX_WIDTH : MAX_WIDTH;

  // Mouse-tracking spotlight state
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const rafRef = useRef<number>(0);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const rect = stageRef.current?.getBoundingClientRect();
      if (rect) {
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePos({ x, y });
      }
      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setContainerSize({
        w: entry.contentRect.width,
        h: entry.contentRect.height,
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const preview = usePreviewedCouncil(query, topology);

  // Konsey Cognitive Renaissance — Bölüm D4. Avatar tıklanınca açılır.
  const openBrainModal = useDebateStore((s) => s.openBrainModal);

  const isLive = seatOrder.length > 0;
  const previewActive = !isLive && preview.value !== null;
  const previewPersonas: Persona[] = preview.value?.personas ?? [];
  const previewSeatOrder: string[] = preview.value?.seatOrder ?? [];
  const ghostMode = !isLive && !previewActive;

  const renderSeats = isLive ? seatOrder : previewActive ? previewSeatOrder : [];

  // Council snapshot for the debate-scoped avatar allocator.
  // Live mode pulls from the keyed `personas` map; preview mode uses the
  // wizard's projected council. We collect ONLY personas that actually
  // render this frame so the allocator's collision map mirrors the
  // visible set (e.g. a dropped persona frees its slot for a peer).
  const councilPersonas = useMemo<Persona[]>(() => {
    if (isLive) {
      return renderSeats
        .map((id) => personas[id])
        .filter((p): p is Persona => Boolean(p));
    }
    if (previewActive) {
      return previewPersonas;
    }
    return [];
  }, [isLive, previewActive, renderSeats, personas, previewPersonas]);

  const stageShape = pickStageShape(renderSeats.length || GHOST_SEATS);

  // Stage outer offset (legacy 28-px breathing room above the disc so
  // the round-pill chip in the top-right doesn't clip the rim). In
  // present mode the chip is hidden by the global HUD and we want
  // every available pixel for the table — drop the offset to 0.
  const stageMarginTop = presentMode ? 0 : 28;

  // Derive the rendered stage width from the container box, the chosen
  // shape, and the mode-aware ceiling.
  //
  // Why both axes matter:
  //   • CIRCLE — width === height (a circle is square). If we only
  //     clamped against parentWidth, a 1600-wide cell with a 700-tall
  //     row would render a 1600×1600 disc that overflows the row by
  //     900 px, getting clipped by `overflow-hidden` on the parent.
  //   • BOARDROOM — height = width × BOARDROOM_AR (≈ 0.5625). The
  //     vertical constraint is therefore (parentHeight / BOARDROOM_AR)
  //     so the table grows until either the long edge or the short
  //     edge runs out of room.
  //
  // The `stageMarginTop` is subtracted from the height budget because
  // the disc's inner box starts that many pixels below the container
  // top — without the deduction the disc overflows the parent's
  // `overflow-hidden` chain by exactly the margin amount.
  //
  // The `Math.max(MIN_WIDTH, ...)` floor preserves the legacy minimum
  // so the table is still useful while the wizard is open and the
  // grid cell is briefly very narrow. The ceiling (`maxWidth`) is the
  // mode-aware cap (760 idle, 1600 present).
  // Boardroom oranı sunum modunda daha kare (BOARDROOM_AR_PRESENT)
  // — sahne dikey alanın yarısını boş bırakmasın.
  const boardroomAr = presentMode ? BOARDROOM_AR_PRESENT : BOARDROOM_AR_NORMAL;

  const width = useMemo(() => {
    const cw = containerSize.w;
    const ch = Math.max(0, containerSize.h - stageMarginTop);
    if (cw <= 0 || ch <= 0) return MIN_WIDTH;
    const verticalCap =
      stageShape === "boardroom" ? ch / boardroomAr : ch;
    const desired = Math.min(cw, verticalCap);
    return Math.max(MIN_WIDTH, Math.min(maxWidth, desired));
  }, [containerSize, stageShape, maxWidth, stageMarginTop, boardroomAr]);

  const height = stageShape === "boardroom" ? Math.round(width * boardroomAr) : width;

  // Konsey Cognitive Renaissance — Bölüm B5.
  // Sahnenin canlı genişliğini body üzerine `--present-stage-w` CSS
  // değişkeni olarak yaz. PresentModeShell `TopicBanner` bunu okur ve
  // sol kenarını sahneye basmayacak şekilde clamp'ler. Sunum modu
  // kapalıyken değişkeni temizle (sıfırla) — diğer tüketiciler
  // fallback (0px) davranışı ile çalışır.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (presentMode) {
      document.body.style.setProperty("--present-stage-w", `${width}px`);
    } else {
      document.body.style.removeProperty("--present-stage-w");
    }
    return () => {
      if (typeof document !== "undefined") {
        document.body.style.removeProperty("--present-stage-w");
      }
    };
  }, [presentMode, width]);

  // Avatar disc diameter (px) — sahneye orantılı, dinamik.
  //
  // Formula tuning (2026-05c — operatör "küçük" şikayetinden sonra):
  //   • Multiplier 0.12 → 0.14 — sahnenin %14'ü kadar avatar
  //     (önceki %12 sahne büyük olduğunda bile avatar'ı bastırıyordu)
  //   • Min boyut 56 → 72 — laptop ekranında ufak masada bile okunaklı
  //   • Ceiling'ler artırıldı (PRESENT mode):
  //       ≤4 seat:  192 → 240 px
  //       5-6 seat: 156 → 192 px
  //       7+ seat:  128 → 152 px (hala 8 koltuk halo collision'a izin vermez)
  //   • Normal mod ceiling'ler de yükseltildi (operasyonel kullanım):
  //       ≤4: 144 → 180
  //       5-6: 116 → 148
  //       7+: 96 → 120
  //   • Boardroom multiplier 0.06 → 0.07; min 40 → 52, max 64 → 80
  const seatCount = renderSeats.length || GHOST_SEATS;
  const circleAvatarCeiling = presentMode
    ? seatCount >= 7
      ? 152
      : seatCount >= 5
      ? 192
      : 240
    : seatCount >= 7
    ? 120
    : seatCount >= 5
    ? 148
    : 180;
  const stageAvatarSize =
    stageShape === "boardroom"
      ? Math.max(52, Math.min(80, width * 0.07))
      : Math.max(72, Math.min(circleAvatarCeiling, width * 0.14));
  // 30-px bleed accounts for the speaking halo (size+28 outer ring)
  // + label stack (display name + expertise on two lines, ~36 px).
  // Boardroom seats sit on long edges, not the rim — keep the legacy
  // 60-px sentinel for it.
  const seatPadding =
    stageShape === "boardroom" ? 60 : Math.round(stageAvatarSize / 2 + 30);

  const positions = useStageLayout({
    seatCount: renderSeats.length || 0,
    width,
    height,
    shape: stageShape,
    seatPadding,
  });

  const labelBudgets = useMemo(
    () => computeLabelBudgets(stageShape, renderSeats.length, width),
    [stageShape, renderSeats.length, width],
  );

  const focusSpeakerId = !muted
    ? audibleSpeakerId ?? currentSpeakerId ?? previousSpeakerId
    : currentSpeakerId ?? previousSpeakerId;

  const speaker = focusSpeakerId ? personas[focusSpeakerId] ?? null : null;
  const previousSpeaker =
    !speaker && previousSpeakerId ? personas[previousSpeakerId] ?? null : null;
  const reduceMotion = useReducedMotion();

  const spotlightSeatIndex = useMemo(() => {
    if (!focusSpeakerId) return -1;
    return renderSeats.indexOf(focusSpeakerId);
  }, [focusSpeakerId, renderSeats]);

  const spotlight = useMemo(() => {
    const pos =
      spotlightSeatIndex >= 0 ? positions[spotlightSeatIndex] : undefined;
    if (!pos) return { x: 50, y: 30, intensity: 0 };
    const cx = 50 + (pos.x / width) * 100;
    const cy = 50 + (pos.y / height) * 100;
    return {
      x: Math.max(10, Math.min(90, cx)),
      y: Math.max(10, Math.min(90, cy)),
      intensity: speaker ? 1 : 0.55,
    };
  }, [spotlightSeatIndex, positions, width, height, speaker]);

  // Track speaker changes for flash effect
  const prevSpeakerRef = useRef<string | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  useEffect(() => {
    if (focusSpeakerId && focusSpeakerId !== prevSpeakerRef.current && isLive) {
      setShowFlash(true);
      const timer = setTimeout(() => setShowFlash(false), 400);
      prevSpeakerRef.current = focusSpeakerId;
      return () => clearTimeout(timer);
    }
    prevSpeakerRef.current = focusSpeakerId ?? null;
  }, [focusSpeakerId, isLive]);

  const borderRadius = stageShape === "boardroom" ? "2.5rem" : "50%";
  const outerBorderRadius = stageShape === "boardroom" ? "3rem" : "50%";

  return (
    <AvatarAllocationProvider personas={councilPersonas}>
    <div
      ref={containerRef}
      data-roundtable
      className="relative grid h-full w-full place-items-center overflow-visible"
      style={{ minHeight: MIN_WIDTH }}
    >
      {/* Round indicator (top-right) */}
      <div className="absolute right-2 top-2 z-10 flex items-center gap-2">
        {isLive ? (
          <Pill tone="accent" monospace>
            {t("stage.activeRound", { n: Math.max(1, roundIndex), max: maxRounds })}
          </Pill>
        ) : null}
        {/* Konsey Cognitive Renaissance — Bölüm B4.
            Pill yerine TermPill — hover'da glossary tooltip açılır
            (consensus / deadlock / max_rounds tanımları). */}
        {status === "finalized" && finalizeReason === "consensus" ? (
          <TermPill term="consensus" tone="success" monospace icon={<CheckCircle2 className="h-3 w-3" aria-hidden />}>
            Konsensüs · erken
          </TermPill>
        ) : null}
        {status === "finalized" && finalizeReason === "deadlock" ? (
          <TermPill term="deadlock" tone="warn" monospace icon={<ShieldAlert className="h-3 w-3" aria-hidden />}>
            Deadlock · erken
          </TermPill>
        ) : null}
        {status === "finalized" && finalizeReason === "max_rounds" ? (
          <TermPill term="max_rounds" tone="info" monospace icon={<Hourglass className="h-3 w-3" aria-hidden />}>
            Tur Bütçesi Tükendi
          </TermPill>
        ) : null}
        {status === "finalized" && finalizeReason === "user" ? (
          <Pill tone="neutral" monospace icon={<StopCircle className="h-3 w-3" aria-hidden />}>
            Kullanıcı durdurdu
          </Pill>
        ) : null}
        {lifecycle.status === "provisioning" ? (
          <Pill tone="info" icon={<Loader2 className="h-3 w-3 animate-spin" aria-hidden />}>
            {t("status.provisioning")}
          </Pill>
        ) : null}
      </div>

      <div
        ref={stageRef}
        className="relative overflow-visible"
        style={{
          width,
          height,
          marginTop: stageMarginTop,
          perspective: "800px",
        }}
        role="group"
        aria-label={t("stage.label")}
        onMouseMove={handleMouseMove}
      >
        {/* === LAYER 0: 3D perspective wrapper for the table === */}
        <div
          className="absolute"
          style={{
            inset: stageShape === "boardroom" ? "10px 16px" : "48px",
            borderRadius,
            transform: "rotateX(2deg)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* === LAYER 0.5: AI Robotic Rings === */}
          {/* Outer rotating segmented ring */}
          <div
            className="absolute -inset-[4px] ai-ring-outer"
            style={{ borderRadius: outerBorderRadius }}
            aria-hidden
          />
          {/* Inner counter-rotating ring */}
          <div
            className="absolute -inset-[2px] ai-ring-inner"
            style={{ borderRadius: outerBorderRadius, opacity: 0.7 }}
            aria-hidden
          />
          {/* Outer glow haze */}
          <div
            className="absolute -inset-[6px] ai-ring-outer"
            style={{
              borderRadius: outerBorderRadius,
              filter: "blur(6px)",
              opacity: 0.35,
            }}
            aria-hidden
          />

          {/* === LAYER 1: Animated conic-gradient border === */}
          <div
            className="absolute -inset-[2px] stage-conic-border"
            style={{ borderRadius: outerBorderRadius }}
            aria-hidden
          />

          {/* === LAYER 2: Main table surface (3-layer glass) === */}
          <div
            className={cn(
              "absolute inset-0",
              "border border-white/[0.06]",
              "shadow-[inset_0_2px_40px_-10px_rgb(0_0_0/0.7),inset_0_0_0_1px_hsl(0_0%_100%/0.02),0_12px_48px_-8px_rgb(0_0_0/0.6)]",
              "backdrop-blur-md",
            )}
            style={{
              borderRadius,
              background: `
                radial-gradient(ellipse at 50% 15%, hsl(var(--accent) / 0.12), transparent 55%),
                radial-gradient(ellipse at 50% 140%, hsl(var(--accent-2) / 0.08), transparent 55%),
                linear-gradient(180deg, hsl(220 22% 9% / 0.92), hsl(220 28% 4% / 0.97))
              `,
            }}
            aria-hidden
          />

          {/* === LAYER 2.5: Radar sweep scanner === */}
          <div
            className="absolute inset-0 pointer-events-none ai-sweep"
            style={{ borderRadius }}
            aria-hidden
          />
          {/* AI core glow (pulsing center energy) */}
          <div
            className="absolute inset-0 pointer-events-none ai-core-glow"
            style={{ borderRadius }}
            aria-hidden
          />
          {/* Hexagonal grid overlay */}
          <div
            className="absolute inset-0 pointer-events-none ai-hex-grid"
            style={{ borderRadius, opacity: 0.5 }}
            aria-hidden
          />
          {/* Circuit trace lines */}
          <div
            className="absolute inset-0 pointer-events-none ai-circuit-pattern"
            style={{ borderRadius, opacity: 0.4 }}
            aria-hidden
          />

          {/* === LAYER 3: Grain noise texture overlay === */}
          <div
            className="absolute inset-0 pointer-events-none mix-blend-overlay"
            style={{
              borderRadius,
              opacity: "var(--stage-noise-opacity)",
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
            aria-hidden
          />

          {/* === LAYER 4: Dot-grid ambient pattern === */}
          <div
            className="absolute inset-0 pointer-events-none dot-grid-pattern"
            style={{ borderRadius }}
            aria-hidden
          />

          {/* === LAYER 4.5: Orbiting AI data nodes === */}
          {[...Array(6)].map((_, i) => {
            const baseR = stageShape === "boardroom"
              ? Math.min(width * 0.42, height * 0.42)
              : width * 0.23;
            const rOffset = i % 2 === 0 ? 6 : -4;
            return (
              <div
                key={`ai-node-${i}`}
                className="ai-data-node"
                style={{
                  "--orbit-r": `${baseR + rOffset}px`,
                  "--orbit-duration": `${5 + i * 1.5}s`,
                  "--blink-duration": `${1.5 + i * 0.4}s`,
                  "--node-size": `${3 + (i % 3) * 1.5}px`,
                  animationDelay: `${-i * 1.2}s`,
                  animationDirection: i % 2 === 0 ? "normal" : "reverse",
                } as React.CSSProperties}
                aria-hidden
              />
            );
          })}
        </div>

        {/* === LAYER 5: Mouse-tracking ambient glow === */}
        <div
          className="absolute pointer-events-none transition-opacity duration-300"
          style={{
            inset: stageShape === "boardroom" ? "10px 16px" : "48px",
            borderRadius,
            opacity: isLive ? 0.4 : 0.15,
            background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, hsl(var(--accent) / 0.12), transparent 40%)`,
          }}
          aria-hidden
        />

        {/* === LAYER 6: Speaker spotlight (triple-layer) === */}
        {isLive ? (
          <>
            {/* Primary persona-tinted cone */}
            <motion.div
              className="pointer-events-none absolute"
              style={{
                inset: stageShape === "boardroom" ? "10px 16px" : "48px",
                borderRadius,
              }}
              initial={false}
              animate={{
                background: `radial-gradient(ellipse at ${spotlight.x}% ${spotlight.y}%, hsl(var(--accent) / ${(0.28 * spotlight.intensity).toFixed(2)}), transparent 42%)`,
              }}
              transition={
                reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 50, damping: 22 }
              }
              aria-hidden
            />
            {/* Secondary cool wash */}
            <motion.div
              className="pointer-events-none absolute"
              style={{
                inset: stageShape === "boardroom" ? "10px 16px" : "48px",
                borderRadius,
              }}
              initial={false}
              animate={{
                background: `radial-gradient(ellipse at ${spotlight.x}% ${spotlight.y}%, hsl(210 50% 70% / ${(0.06 * spotlight.intensity).toFixed(2)}), transparent 30%)`,
              }}
              transition={
                reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 40, damping: 25 }
              }
              aria-hidden
            />
            {/* Speaker-change flash */}
            {showFlash && (
              <div
                className="pointer-events-none absolute spotlight-flash"
                style={{
                  inset: stageShape === "boardroom" ? "10px 16px" : "48px",
                  borderRadius,
                  background: `radial-gradient(ellipse at ${spotlight.x}% ${spotlight.y}%, hsl(var(--accent) / 0.3), transparent 50%)`,
                }}
                aria-hidden
              />
            )}
            {/* Ambient breathing glow (idle state — no active speaker) */}
            {!speaker && (
              <div
                className="pointer-events-none absolute"
                style={{
                  inset: stageShape === "boardroom" ? "8px 14px" : "44px",
                  borderRadius: outerBorderRadius,
                  animation: "idle-breathe 4s ease-in-out infinite",
                  boxShadow: "inset 0 0 80px -20px hsl(var(--accent) / 0.08)",
                }}
                aria-hidden
              />
            )}
          </>
        ) : null}

        {/* Edges */}
        <TopologyEdgeRenderer
          width={width}
          height={height}
          shape={stageShape}
          topology={topology}
          previewSeatCount={previewSeatOrder.length}
        />

        {/* MERKEZ SAHNE STRATEJİSİ — operatör revizyonu (2026-05-e)
         *   Klasik mod  → konuşmacı yokken CenterPhaseBoard, varken
         *                 dekoratif CenterMonogram (tarihsel "ortada
         *                 speaker olmasın" doktrini korunur).
         *   Sunum modu  → konuşmacı varken `CenterStageSpotlight` her
         *                 topolojide aktif. Eski `stageShape === "circle"`
         *                 filtresi (operatör 2026-05-e geri bildirimi:
         *                 "boardroom'da ortada konuşan yok") kaldırıldı.
         *                 Spotlight artık `stageHeight` ile boyutlanır:
         *                 boardroom yatay-ovalinde de identity card alt
         *                 koltuk sırasını ezmez. */}
        {!speaker ? (
          <CenterPhaseBoard
            speaker={null}
            previousSpeaker={previousSpeaker}
            ghostMode={ghostMode}
            isLive={isLive}
            phase={currentPhase}
          />
        ) : presentMode ? (
          <CenterStageSpotlight
            persona={speaker}
            baseAvatarSize={stageAvatarSize}
            stageWidth={width}
            stageHeight={height}
            isAudible={!muted && audibleSpeakerId === speaker.id}
          />
        ) : (
          <CenterMonogram />
        )}

        {/* Seats */}
        {ghostMode
          ? Array.from({ length: GHOST_SEATS }).map((_, i) => (
              <GhostSeat key={`g${i}`} size={width} idx={i} total={GHOST_SEATS} />
            ))
          : null}

        {!ghostMode &&
          renderSeats.map((id, i) => {
            const persona = isLive ? personas[id] : previewPersonas.find((p) => p.id === id);
            const pos = positions[i];
            if (!persona || !pos) return null;
            return (
              <div
                key={id}
                className="absolute"
                style={{
                  left: `calc(50% + ${pos.x}px)`,
                  top: `calc(50% + ${pos.y}px)`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <AgentAvatar
                  persona={persona}
                  size={stageAvatarSize}
                  preview={!isLive}
                  // Konsey Cognitive Renaissance — Bölüm D4/17.K.
                  // Live mode'da avatar tıklanınca BrainModal açılır.
                  {...(isLive ? { onPersonaClick: openBrainModal } : {})}
                  {...(labelBudgets?.[i] !== undefined
                    ? { labelMaxWidth: labelBudgets[i] }
                    : {})}
                />
                {/* BrainBadge — cognitive_load > 50 ise nabız + tıklatma
                    için BrainModal'a yönlendirir. Boyut sahne'ye
                    orantılı (D5). */}
                {isLive ? (
                  <BrainBadgeOverlay
                    personaId={persona.id}
                    avatarSize={stageAvatarSize}
                    onClick={() => openBrainModal(persona.id)}
                  />
                ) : null}
              </div>
            );
          })}
      </div>
    </div>
    </AvatarAllocationProvider>
  );
}
